import { scheme } from '@starryui10/theme/scheme';
import React, { FC, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Text } from '@starryui10/typography/Text';
import { useState } from 'react';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import { ReactPortal } from '@starryui10/layouts/ReactPortal';

interface ITooltipProps {
    caption: string;
    placement?: Placement;
    offset?: any;
    maxWidth?: string;
    children: (visibleFunc: React.Dispatch<React.SetStateAction<boolean>>) => React.ReactNode;
}

export const Tooltip: FC<ITooltipProps> = ({ offset = [0, 4], caption, children, placement = 'bottom-start', maxWidth = '30ch' }) => {
    const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLSpanElement | null>(null);
    const [arrowElement, setArrowElement] = useState<HTMLSpanElement | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const handleDocumentClick = () => {
            setVisible(false);
        };

        if (visible) {
            document.addEventListener('click', handleDocumentClick);
        }

        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [visible]);

    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement,
        modifiers: [
            { name: 'arrow', options: { element: arrowElement, padding: 0 } },
            {
                name: 'offset',
                options: {
                    offset: offset,
                },
            },
        ],
    });

    return (
        <>
            <span style={{ display: 'inline-block', height: '100%' }} ref={setReferenceElement}>
                {children(setVisible)}
            </span>

            {visible && (
                <ReactPortal>
                    <span ref={setPopperElement} style={styles.popper} {...attributes.popper}>
                        <StyledDropdown maxWidth={maxWidth}>
                            <span ref={setArrowElement} style={{ ...styles.arrow, top: styles.popper.top === '0' ? '0' : undefined }}>
                                <StyledArrow positionTop={styles.popper.top} />
                            </span>
                            <Text color="inherit" variant="small.regular" textAlign="center">
                                {caption}
                            </Text>
                        </StyledDropdown>
                    </span>
                </ReactPortal>
            )}
        </>
    );
};

const showOpacity = keyframes`
    0% { opacity: 0 }
    100% { opacity: 1; }
`;

const StyledDropdown = styled.span<{ maxWidth: string }>`
    ${scheme.Grey.Darker}
    box-shadow: 0px 2px 5px rgba(39, 40, 51, 0.2);
    border-radius: 4px;
    display: inline-flex;
    padding: 8px 24px;
    animation-name: ${showOpacity};
    animation-duration: 0.2s;
    animation-iteration-count: forwards;
    line-height: 0;
    max-width: ${(props) => props.maxWidth};
    word-wrap: normal;
    width: max-content;
`;

const StyledArrow = styled.span<{ positionTop: string | number | undefined }>`
    transform: translateY(-2px);
    transform-origin: center;
    display: inline-flex;
    padding: 0;
    margin: 0;
    line-height: 0;

    &:before {
        ${scheme.Grey.Darker}

        content: '';
        height: 6px;
        width: 6px;
        transform: ${(props) => (props.positionTop === '0' ? 'rotate(135deg)' : 'rotate(-45deg)')};
        transform-origin: center;
        display: inline-block;
        z-index: -1;
        position: relative;
        line-height: 0;
    }
`;
